/**
 * The work-authorisation banner.
 *
 * Shown on the dashboard and on an employee profile. Two rules govern it:
 *
 *  1. IT DOES NOT APPEAR FOR CITIZENS AND PERMANENT RESIDENTS. `NOT_REQUIRED` is the
 *     status for most of the company; a permanent "you are authorised to work" banner is
 *     noise, and noise is what makes people stop reading banners that matter.
 *
 *  2. IT NEVER DERIVES "EXPIRED" FROM `expiryDate`. A cap-gap, an H-1B portability
 *     transfer or an EAD auto-extension makes today lawful even when the document on file
 *     has lapsed. Only the server's `decision.authorized` answers the question, and
 *     `decision.continuation` explains why an expired-looking document is still fine.
 *     Getting that wrong tells a lawfully-employed person they cannot work.
 */
import { AlertTriangle, ShieldAlert, ShieldCheck } from 'lucide-react';

import type { AuthorizationDecision, WorkAuthRecord } from '@frontend/api/hooks/workAuth';
import { formatDate } from '@frontend/lib/format';
import { Banner } from '@frontend/components/ui/Page';

export function WorkAuthBanner({
  record,
  decision,
  className,
  isHrView = false,
  action,
}: {
  record: WorkAuthRecord | null | undefined;
  decision?: AuthorizationDecision | null;
  className?: string;
  /** HR sees the "no record on file" prompt; the employee does not. */
  isHrView?: boolean;
  /**
   * The way OUT of the problem the banner describes — the "Add record" button.
   *
   * The banner said "Add a record" and gave the reader nowhere to do it, which is a
   * dead-end empty state in banner form. It is a prop rather than a hardcoded link so the
   * banner stays usable on the onboarding checklist as well as the employee record.
   */
  action?: JSX.Element;
}): JSX.Element | null {
  // No record at all.
  if (record === null || record === undefined) {
    if (!isHrView) return null;
    return (
      <Banner tone="warning" className={className} title="No work authorisation record on file" data-testid="work-auth-banner">
        This employee cannot be verified as authorised to work, and cannot be assigned to a project. Add a
        record — for a US citizen or permanent resident it takes one field and is marked "not required"
        thereafter.
        {action !== undefined && <div className="mt-2">{action}</div>}
      </Banner>
    );
  }

  // Citizens / LPRs: nothing to track, nothing to say.
  if (record.status === 'NOT_REQUIRED') return null;

  // The server's decision is authoritative. Prefer it over any local date arithmetic.
  if (decision != null && !decision.authorized) {
    return (
      <Banner tone="error" className={className} title="Not authorised to work" data-testid="work-auth-banner">
        <span className="flex items-start gap-1.5">
          <ShieldAlert aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {decision.message} This person cannot be assigned to new project work until it is resolved.
            Contact HR or immigration counsel.
          </span>
        </span>
      </Banner>
    );
  }

  // Authorised on a statutory continuation — watch, do not block.
  if (decision?.continuation != null) {
    return (
      <Banner
        tone="info"
        className={className}
        title="Authorised on a statutory continuation"
        data-testid="work-auth-banner"
      >
        <span className="flex items-start gap-1.5">
          <ShieldCheck aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {decision.continuation.explanation}
            {decision.continuation.endDate !== null && (
              <> This bridge runs to {formatDate(decision.continuation.endDate)}.</>
            )}{' '}
            Work may continue — do not block assignments on the document expiry date alone.
          </span>
        </span>
      </Banner>
    );
  }

  const daysRemaining = decision?.daysRemaining ?? null;

  if (daysRemaining !== null && daysRemaining <= 30) {
    return (
      <Banner tone="error" className={className} title="Work authorisation expires very soon" data-testid="work-auth-banner">
        <span className="flex items-start gap-1.5">
          <AlertTriangle aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {record.authType} expires {formatDate(record.expiryDate)} — {daysRemaining} day
            {daysRemaining === 1 ? '' : 's'} remaining. Immigration counsel should already be engaged.
          </span>
        </span>
      </Banner>
    );
  }

  if (daysRemaining !== null && daysRemaining <= 90) {
    return (
      <Banner tone="warning" className={className} title="Work authorisation renewal due" data-testid="work-auth-banner">
        {record.authType} expires {formatDate(record.expiryDate)} — {daysRemaining} days remaining. Renewal
        should be started now; most categories take longer than this to process.
      </Banner>
    );
  }

  if (record.status === 'PENDING_VERIFICATION') {
    return (
      <Banner tone="warning" className={className} title="Work authorisation not yet verified" data-testid="work-auth-banner">
        HR has not verified the documents on file. The employee cannot be activated until they do.
      </Banner>
    );
  }

  return null;
}
